<template>
  <div class="space-y-6">
    <ReportTitle :title="$gettext('Rapport de location')" :date="report?.attributes?.createdAt" />
    <div v-if="reportData" class="nobreak">
      <h3 class="text-lg font-medium text-gray-800 mb-2">{{ $gettext("Information générale") }}</h3>
      <div class="max-w-md">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt class="font-medium text-gray-700">{{ $gettext("Date de fin") }}:</dt>
          <dd class="text-gray-900 font-bold">
            {{ parseDate(reportData?.attributes?.endAt) }}
          </dd>
          <dt class="font-medium text-gray-700">{{ $gettext("Commission") }}:</dt>
          <dd class="text-gray-900 font-bold">
            {{ reportData?.attributes?.commission }}
            {{ commissionTypeSymbol(reportData?.relationships?.commissionType?.data?.attributes?.name) }}
          </dd>
        </dl>
      </div>
    </div>
    <div v-if="reportData">
      <div class="max-w-md">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt class="text-gray-700">
            {{ $gettext("Bailleur(s)") }}:
          </dt>
          <dd class="text-gray-900 font-bold">
            <p v-for="(landlord, index) in reportData?.relationships?.rentalListingAgreementLandlords?.data"
              :key="index">
              {{ landlord?.attributes?.name }}
              <span :class="badgeClassFor(landlord?.attributes?.riskScoringLevel)">
                {{ badgeTextFor(landlord?.attributes?.riskScoringLevel) }}
              </span>
            </p>
          </dd>
        </dl>
      </div>
      <div>
        <h3 class="text-lg font-medium text-gray-800 mb-4">{{ $gettext("Propriétés") }}</h3>
        <div v-for="propertyAgreement in rentalProperties" :key="propertyAgreement?.id"
          class="border rounder-md border-1 border-black p-4 mb-4">
          <div class="max-w-md">
            <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt class="font-medium text-gray-700">{{ $gettext("Adresse") }}:</dt>
              <dd class="text-gray-900 font-bold">
                {{ propertyAgreement?.relationships?.property?.data?.attributes?.fullAddress }}
              </dd>
              <dt class="font-medium text-gray-700">{{ $gettext("Loyer de commercialisation") }}:</dt>
              <dd class="text-gray-900 font-bold">
                {{ propertyAgreement?.attributes?.listingRent }}€
              </dd>
            </dl>
          </div>

          <div v-if="propertyAgreement?.relationships?.property?.data?.relationships?.leaseAgreements?.data?.length"
            class="mt-4">
            <h5 class="text-lg font-medium text-gray-800 mb-4">{{ $gettext("Bail(s)") }}</h5>
            <div v-for="lease in propertyAgreement?.relationships?.property?.data?.relationships?.leaseAgreements?.data"
              :key="lease?.id" class="border rounder-md border-1 border-black p-4">
              <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt class="font-medium text-gray-700">{{ $gettext("Date de signature") }}:</dt>
                <dd class="text-gray-900 font-bold">
                  {{ parseDate(lease?.attributes?.signatureDate) }}
                </dd>
                <dt class="font-medium text-gray-700">{{ $gettext("Type de bail") }}:</dt>
                <dd class="text-gray-900 font-bold">
                  {{ lease?.relationships?.leaseType?.data?.attributes?.name }}
                </dd>
                <dt class="font-medium text-gray-700">{{ $gettext("Montant du loyer") }}:</dt>
                <dd class="text-gray-900 font-bold">
                  {{ lease?.attributes?.price }}€
                </dd>
                <dt class="font-medium text-gray-700">{{ $gettext("Date de fin") }}:</dt>
                <dd class="text-gray-900 font-bold">
                  {{ parseDate(lease?.attributes?.endDate) }}
                </dd>
                <dt class="text-gray-700">
                  {{ $gettext("Locataire(s)") }}:
                </dt>
                <dd class="text-gray-900 font-bold">
                  <p v-for="(tenant, index) in lease?.relationships?.tenants?.data" :key="index">
                    {{ tenant?.attributes?.name }}
                    <span :class="badgeClassFor(tenant?.attributes?.riskScoringLevel)">
                      {{ badgeTextFor(tenant?.attributes?.riskScoringLevel) }}
                    </span>
                  </p>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <ReportRiskScoringBlock :riskScoring="riskScoring" :showSanctionsBlock="false" />
        <ReportDocumentsBlock :documents="documents" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useDocumentUtils } from "@/utils/documents";
import { commissionTypeSymbol } from "@/utils/commissionType";
import ReportTitle from "@/components/reports/ReportTitle.vue";
import ReportDocumentsBlock from "@/components/reports/ReportDocumentsBlock.vue";
import { useGettext } from "vue3-gettext";
import ReportRiskScoringBlock from "./ReportRiskScoringBlock.vue";
import { useRiskBadges } from "@/utils/reports_badges.js";
import { useDate } from "@/utils/stores/date";

const { parseDate } = useDate();
const { badgeTextFor, badgeClassFor } = useRiskBadges();
const { $gettext } = useGettext();
const props = defineProps({
  report: {
    type: Object,
    required: true
  }
});

const documents = useDocumentUtils().extractDocuments(props.report);
const reportData = props.report?.attributes?.data;
const rentalProperties = reportData?.relationships?.rentalListingAgreementProperties?.data || [];
const riskScoring =
  reportData?.relationships?.lastRiskScoring?.data
    ? reportData.relationships.lastRiskScoring.data
    : null;
</script>
